/** Step 4 — 确认并提交 */
import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAppSettings } from '../../components/providers/AppSettingsProvider';
import { loadAuthUser } from '../../services/auth';
import { getUserTimezone, submitManualCheckIn } from '../../services/exploreService';
import { cacheManualCheckInAfterSubmit } from '../../services/checkInListCache';
import { createPageUrl } from '../../utils';
import { CheckInShell } from './components/CheckInShell';
import { getPresetCheckInEvent } from './data/events';
import { moodPillVariantClass, moodVariantForTag } from './data/moods';

const TITLE_MAX = 255;
const THOUGHTS_MAX = 2000;

export type CheckInSummaryLocationState = {
  moodTag: string;
  activityTag: string;
  /** 自定义事件时传入 */
  activityEmoji?: string;
};

const CheckInSummary: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useAppSettings();
  const state = location.state as CheckInSummaryLocationState | null;

  const moodTag = state?.moodTag;
  const activityTag = state?.activityTag;
  const activityEmoji = state?.activityEmoji;

  const [journalTitle, setJournalTitle] = useState('');
  const [journalText, setJournalText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!loadAuthUser()) {
      navigate('/splash', { replace: true, state: { forceLogin: true } });
    }
  }, [navigate]);

  useEffect(() => {
    if (!moodTag || !activityTag) navigate('/check-in', { replace: true });
  }, [moodTag, activityTag, navigate]);

  const moodLabel = useCallback(
    () => (moodTag ? t(`checkIn.tags.${moodTag}`) || moodTag : ''),
    [moodTag, t],
  );

  const eventLabel = useCallback(() => {
    if (activityEmoji) return activityTag;
    return t(`checkIn.events.${activityTag}`) || activityTag;
  }, [activityEmoji, activityTag, t]);

  if (!moodTag || !activityTag) return null;

  const presetEvent = activityEmoji || !activityTag ? null : getPresetCheckInEvent(activityTag);
  const moodVariant = moodTag ? moodVariantForTag(moodTag) : null;
  const moodClass = moodVariant ? moodPillVariantClass[moodVariant] : moodPillVariantClass.gray;
  const EventIcon = presetEvent?.icon;

  const handleComplete = async () => {
    if (!moodTag || !activityTag || submitting) return;
    setSubmitting(true);
    try {
      const tz = getUserTimezone();
      const title = journalTitle.trim();
      const thoughts = journalText.trim();
      const result = await submitManualCheckIn({
        emotion_tag: moodTag,
        activity_tag: activityTag,
        activity_emoji: activityEmoji || undefined,
        journal_title: title || undefined,
        journal_text: thoughts || undefined,
        timezone: tz,
      });
      if (!result) { toast.error(t('checkIn.submitError')); return; }
      cacheManualCheckInAfterSubmit(
        {
          emotion_tag: moodTag,
          activity_tag: activityTag,
          activity_emoji: activityEmoji || undefined,
          journal_title: title || undefined,
          journal_text: thoughts || undefined,
          timezone: tz,
        },
        result,
      );
      navigate(createPageUrl('Journals'), { state: { fromCheckIn: true } });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <CheckInShell
      ctaLabel={submitting ? t('common.loading') : t('checkIn.completeCheckIn')}
      onCta={() => void handleComplete()}
      ctaDisabled={submitting}
      bodyPb="pb-44"
    >
      <div className="mt-8 w-full space-y-5">
        {/* Mood tag */}
        <div className="flex flex-wrap items-center gap-2.5">
          <span className="text-sm font-medium text-white/80">
            {t('checkIn.summaryMoodLabel')}
          </span>
          <span className={`inline-flex max-w-full rounded-full px-4 py-1.5 text-sm font-medium ${moodClass}`}>
            {moodLabel()}
          </span>
        </div>

        {/* Activity tag */}
        <div className="flex flex-wrap items-center gap-2.5">
          <span className="text-sm font-medium text-white/80">
            {t('checkIn.summaryEventLabel')}
          </span>
          <span className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/[0.12] px-3 py-1.5 text-sm font-medium text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.18)] backdrop-blur-md">
            {activityEmoji ? (
              <span className="text-lg leading-none" aria-hidden>{activityEmoji}</span>
            ) : EventIcon ? (
              <EventIcon className="h-5 w-5 shrink-0 opacity-95" strokeWidth={1.75} aria-hidden />
            ) : null}
            <span>{eventLabel()}</span>
          </span>
        </div>

        {/* Optional journal */}
        <div className="space-y-3 pt-1">
          <input
            type="text"
            value={journalTitle}
            maxLength={TITLE_MAX}
            onChange={(e) => setJournalTitle(e.target.value.slice(0, TITLE_MAX))}
            placeholder={t('checkIn.summaryTitlePlaceholder')}
            className="w-full rounded-2xl border border-white/35 bg-white/[0.12] px-4 py-3 text-[15px] text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.22)] outline-none backdrop-blur-xl placeholder:text-white/40 focus:border-white/50"
            aria-label={t('checkIn.summaryTitlePlaceholder')}
          />
          <textarea
            value={journalText}
            maxLength={THOUGHTS_MAX}
            onChange={(e) => setJournalText(e.target.value.slice(0, THOUGHTS_MAX))}
            placeholder={t('checkIn.summaryThoughtsPlaceholder')}
            rows={5}
            className="w-full resize-none rounded-2xl border border-white/35 bg-white/[0.12] px-4 py-3 text-[15px] leading-relaxed text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.22)] outline-none backdrop-blur-xl placeholder:text-white/40 focus:border-white/50"
            aria-label={t('checkIn.summaryThoughtsPlaceholder')}
          />
        </div>
      </div>
    </CheckInShell>
  );
};

export default CheckInSummary;
